import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs';
import { HapticDirective } from '../../directives/haptic';
import { SheetService } from '../../../core/services/sheet';
import { Notification as NotificationService } from '../../../core/services/notification';

/** Entrée de nav correspondant à l'écran courant. */
export type NavTab = 'home' | 'notifications' | 'profile';

interface NavEntry {
  readonly tab: NavTab;
  readonly route: string;
  readonly icon: string;
  readonly iconSize: string;
  readonly label: string;
  readonly ariaLabel: string;
}

/** Écrans qui portent la nav. Ailleurs — création, détail, auth — elle s'efface. */
const NAV_ROUTES: Record<NavTab, string> = {
  home: '/home',
  notifications: '/notifications',
  profile: '/profile',
};

/**
 * Nav flottante — pilule de verre posée au-dessus du contenu, présente sur
 * Accueil, Notifications et Profil.
 *
 * Elle est montée **une seule fois**, dans la coquille de l'application, et
 * déduit elle-même l'entrée active de l'URL. Portée par chaque écran, elle était
 * détruite puis reconstruite à chaque navigation : elle disparaissait et
 * réapparaissait en fondu entre deux onglets, alors qu'une barre de navigation
 * doit donner l'impression de ne jamais bouger.
 *
 * Deux partis pris de la maquette à ne pas « corriger » :
 *  • seule l'entrée active porte son libellé, les autres sont icône seule —
 *    c'est ce qui permet à la pilule de rester compacte et centrée ;
 *  • la nav disparaît dès qu'une bottom sheet s'ouvre (§4.2, §8.6), sinon elle
 *    flotterait par-dessus le panneau.
 *
 * Le décalage bas de 22 px de la maquette est complété par la safe area : sur
 * un iPhone à home indicator, 22 px secs placeraient la pilule sous la barre.
 */
@Component({
  selector: 'app-bottom-nav',
  imports: [RouterLink, HapticDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (active(); as current) { @if (!sheets.anyOpen()) {
    <nav
      class="fixed inset-x-0 z-50 mx-auto flex w-max items-center gap-1 rounded-full border border-white/8 p-[7px] shadow-[0_18px_40px_-14px_rgba(0,0,0,0.8)] backdrop-blur-[14px]"
      style="bottom: calc(env(safe-area-inset-bottom, 0px) + 22px); background: rgba(14,15,30,0.88)"
    >
      @for (entry of entries; track entry.tab) {
      <a
        [routerLink]="entry.route"
        appHaptic
        [attr.aria-label]="entry.ariaLabel"
        [attr.aria-current]="current === entry.tab ? 'page' : null"
        class="relative inline-flex items-center gap-[7px] rounded-full text-[12px] font-extrabold transition-all duration-250 active:scale-[0.93]"
        [class]="pillClass(entry.tab)"
      >
        <span class="material-icons-round" [style.font-size]="entry.iconSize">{{
          entry.icon
        }}</span>
        @if (current === entry.tab) {
        {{ entry.label }}
        } @if (entry.tab === 'notifications' && hasUnread()) {
        <span class="absolute top-[9px] right-[10px] h-1.5 w-1.5 rounded-full bg-amber"></span>
        }
      </a>

      @if (entry.tab === 'home') {
      <a
        routerLink="/events/new"
        appHaptic="medium"
        aria-label="Créer un événement"
        class="grid h-12 w-12 place-items-center rounded-full shadow-[0_8px_20px_-6px_rgba(255,177,61,0.75)] transition-transform active:scale-90"
        style="background: linear-gradient(140deg,#FFB13D,#F08A0C)"
      >
        <span class="material-icons-round text-[24px] text-nuit-2">add</span>
      </a>
      } }
    </nav>
    } }
  `,
})
export class BottomNav {
  protected readonly sheets = inject(SheetService);
  private readonly router = inject(Router);
  private readonly notifications = inject(NotificationService);

  /** URL courante, réévaluée à chaque navigation terminée. */
  private readonly url = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects)
    ),
    { initialValue: this.router.url }
  );

  /** Entrée à mettre en avant, ou `null` sur un écran qui ne porte pas la nav. */
  protected readonly active = computed<NavTab | null>(() => {
    const path = this.url().split('?')[0];
    const match = (Object.keys(NAV_ROUTES) as NavTab[]).find(
      (tab) => path === NAV_ROUTES[tab] || path.startsWith(`${NAV_ROUTES[tab]}/`)
    );
    return match ?? null;
  });

  /**
   * Pastille ambre de l'entrée Notifications.
   *
   * Requête dédiée, bornée à UN document. La déduire du fil complet coûtait
   * cent lectures Firestore — et cette barre est montée sur TOUS les écrans,
   * pas seulement l'accueil : c'était donc cent lectures à chaque démarrage,
   * quoi que l'usager ouvre ensuite.
   */
  protected readonly hasUnread = toSignal(this.notifications.watchHasUnread(), {
    initialValue: false,
  });

  protected readonly entries: readonly NavEntry[] = [
    {
      tab: 'home',
      route: NAV_ROUTES.home,
      icon: 'home',
      iconSize: '18px',
      label: 'Accueil',
      ariaLabel: 'Accueil',
    },
    {
      tab: 'notifications',
      route: NAV_ROUTES.notifications,
      icon: 'notifications',
      iconSize: '19px',
      label: 'Activité',
      ariaLabel: 'Notifications',
    },
    {
      tab: 'profile',
      route: NAV_ROUTES.profile,
      icon: 'person',
      iconSize: '19px',
      label: 'Profil',
      ariaLabel: 'Profil',
    },
  ];

  protected pillClass(tab: NavTab): string {
    return this.active() === tab
      ? 'bg-accent text-white px-4 py-2.5'
      : 'bg-transparent text-white/60 px-3 py-2.5';
  }
}
